// FLAGS: identity
// The Phase Two browser session: hand the authorization code to the API, and ask it to end the
// session again. The credential itself is an HttpOnly cookie the API sets and this code cannot
// read (OI-25561) — everything here either starts that cookie, ends it, or records that one exists.
import axios from "axios";
import { apiPaths } from "@src/utils/constants";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import { buildApiRequestHeaders } from "@src/utils/requestHeaders";

/** The API feature flag Phase Two authentication is gated on (`AppConstants.FeatureFlags`). */
export const PHASE_TWO_AUTH_FLAG_KEY = "PhaseTwoAuth";

// Not the session, and not a credential: the session is the cookie. This only records that a
// Phase Two sign-in happened in this browser, which is what the app needs to know when it chooses
// between the Keycloak flows and the legacy ones. An attacker who sets it gains nothing — every
// call still has to be authenticated by the cookie.
//
// It lives in localStorage because the cookie does: the API sets it for the whole browser, so a
// second tab is signed in too, and a marker kept per tab (sessionStorage) left every other tab
// choosing the legacy logout and refresh — a logout that answered 401 and did not take. A record
// that outlives the tab needs clearing by whatever signs in the legacy way instead; see
// userActions' login-success path. The name says what it is, a sign-in that happened, not a
// session or a token: nothing here is worth stealing.
const MARKER_KEY = "phaseTwoSignedIn";

// Where the marker lived before it moved to localStorage. A browser signed in under the old build
// still carries it there; it is honoured until that sign-in ends, and cleared with the new one.
const LEGACY_TAB_MARKER_KEY = "phaseTwoSession";

export const markPhaseTwoSession = (): void => {
  localStorage.setItem(MARKER_KEY, "1");
};

export const hasPhaseTwoSession = (): boolean =>
  localStorage.getItem(MARKER_KEY) !== null ||
  sessionStorage.getItem(LEGACY_TAB_MARKER_KEY) !== null;

export const clearPhaseTwoSession = (): void => {
  localStorage.removeItem(MARKER_KEY);
  sessionStorage.removeItem(LEGACY_TAB_MARKER_KEY);
};

export interface PhaseTwoLogoutResponse {
  federatedLogoutUrl?: string | null;
}

// Bare instance, deliberately. The app's shared interceptors turn a 401 or 403 into refresh-then-
// sign-out, and both endpoints here answer 403 for ordinary reasons — Phase Two switched off, or a
// request without the client header. Running them through the shared stack would sign a user out
// over an answer that was never about their session. `withCredentials` is what lets the browser
// keep the cookie the API sets, and send it back on the way out.
const sessionHttp = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

/**
 * Hands the realm's authorization code and its PKCE verifier to the API, which redeems them and
 * answers with the session cookie. The redirect URI is not sent: the API rebuilds it from this
 * request's own origin, because the WAF rejects request bodies carrying URLs.
 */
export const createPhaseTwoSession = async (
  code: string,
  codeVerifier: string,
): Promise<void> => {
  await sessionHttp.post(
    apiPaths.phaseTwoSession,
    { code, codeVerifier },
    { headers: buildApiRequestHeaders() },
  );

  markPhaseTwoSession();
};

/**
 * Trades the refresh cookie for a new token pair. The API writes the new cookies; nothing comes
 * back in the body, because nothing the browser holds is readable by this code.
 *
 * Called from the shared interceptor's single-flight, and from the two-factor client, which sits
 * outside that stack on purpose. That matters: the realm rotates the refresh token on every use, so
 * two requests presenting the same one would see the second refused. What keeps a collision from
 * ending the session is the realm's `refreshTokenMaxReuse`, 2 on all four realms.
 */
export const refreshPhaseTwoSession = async (): Promise<void> => {
  await sessionHttp.post(apiPaths.phaseTwoSessionRefresh, undefined, {
    headers: buildApiRequestHeaders(),
  });
};

/**
 * Ends the session at the API, which drops the stored tokens, expires the cookie and ends the
 * realm's own session. The returned URL is where the browser goes next to clear the realm's SSO
 * cookie, so the next sign-in authenticates instead of riding it.
 */
export const endPhaseTwoSession = async (): Promise<string | null> => {
  try {
    const { data } = await sessionHttp.delete<PhaseTwoLogoutResponse>(
      apiPaths.phaseTwoSession,
      { headers: buildApiRequestHeaders() },
    );

    return data?.federatedLogoutUrl ?? null;
  } finally {
    // Whatever the API said, this browser is signed out.
    clearPhaseTwoSession();
  }
};
